/**
 * PageTitle keys its effect on the breadcrumbs prop identity, and PageDataProvider does not memoise
 * its context value — a fresh [] on every render re-fires the effect and loops. The constant is
 * hoisted to module level for that reason.
 */
export const NO_BREADCRUMBS: never[] = []

/**
 * Theme variables, not hex: Metronic redefines them for the dark theme, so a block painted with them
 * follows the theme switch on its own. The block itself is Metronic's `card` class.
 */
export const BORDER = 'var(--bs-border-color)'
export const INK = 'var(--bs-gray-900)'
export const BODY = 'var(--bs-gray-800)'
export const SOFT = 'var(--bs-gray-700)'
export const MUTED = 'var(--bs-gray-600)'
export const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace'

/** Raw JSON block — the same surface on both cards. */
export const RAW_BLOCK: React.CSSProperties = {
  padding: '14px 16px',
  borderRadius: 8,
  background: 'var(--bs-gray-100)',
  border: `1px solid ${BORDER}`,
  fontFamily: MONO,
  fontSize: 11.5,
  lineHeight: 1.6,
  color: SOFT,
  overflow: 'auto',
}

/** A row of facts that spreads over the whole width of its block instead of hugging the left edge. */
export const FACT_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '18px 28px',
  padding: '18px 28px',
}

const tone = (name: string) => ({ bg: `var(--bs-${name}-light)`, dot: `var(--bs-${name})` })

/**
 * Colour is the only thing derived from a point's kind — every text arrives ready from the backend,
 * so a kind this build has never heard of still renders, neutrally and with its own title.
 */
export const POINT_TONE: Record<string, { bg: string; dot: string }> = {
  CREATED: tone('primary'),
  MERCHANT_REQUEST: tone('primary'),
  CASCADE_ROUND: tone('info'),
  MATCHING_REFUSED: tone('danger'),
  CLOSED: tone('danger'),
  CALLBACK: tone('success'),
  STAFF_ACTION: tone('warning'),
  REQUEST_REFUSED: tone('primary'),
  PAYOUT_MATCHING_REFUSED: tone('danger'),
  CASCADE_EMPTY: tone('danger'),
  BALANCE_REFUSED: tone('danger'),
  MATCHING_ADMITTED: tone('primary'),
  REQUISITE_REPLACED: tone('warning'),
  TILE_OFFERED: tone('info'),
  DEPOSIT_MATCHED: tone('info'),
  RECEIPT_UPLOADED: tone('info'),
  DISPUTE_OPENED: tone('warning'),
  DISPUTE_RESOLVED: tone('warning'),
  PAYOUT_PAUSED: tone('warning'),
  PAYOUT_RESUMED: tone('success'),
  ALERT_SENT: tone('warning'),
  PAYOUT_HALTED: tone('danger'),
}

export const NEUTRAL_TONE = { bg: 'var(--bs-gray-100)', dot: 'var(--bs-gray-400)' }

/**
 * Only the rules that mean there was nothing to match against are red; the rest are configuration,
 * and a payout the shell dropped before the core saw it is a third thing again — it was never
 * compared with this deposit at all.
 */
export const RULE_BAR_COLOUR = (code: string): string => {
  if (code.startsWith('excluded_')) return 'var(--bs-info)'
  if (code === 'AMOUNT_NOT_IN_ACCEPTABLE_RANGES') return 'var(--bs-danger)'
  if (code === 'NOT_ENOUGH_CAPACITY') return 'var(--bs-info)'
  if (code === 'no_slot' || code === 'exceeds_remainder') return 'var(--bs-danger)'

  return 'var(--bs-warning)'
}

/**
 * Закрытие красят исходом, а не самим фактом: `CONFIRMED_BY_PROVIDER` — успех, и красная плашка
 * на нём читается как авария.
 */
export const CLOSE_TONE = (successful: boolean | null): { bg: string; dot: string; chip: string } =>
  successful
    ? { ...tone('success'), chip: 'var(--bs-success)' }
    : { ...tone('danger'), chip: 'var(--bs-danger)' }

/**
 * Мэтчинг записал решение, но не отказал: клиенту предложили выбор суммы. Красная точка на этом
 * читается как авария, которой не было — а таких решений на проде большинство.
 */
const OFFER_TONE = tone('warning')

const isOffer = (fields: Record<string, unknown>): boolean =>
  Array.isArray(fields.codes) && fields.codes[0] === 'offer'

/**
 * Весь выбор тона точки — здесь, а в разметке остаётся только отрисовка: цвета заданы токенами
 * темы, а `var()` не читается из DOM, поэтому решение проверяемо лишь как чистая функция.
 */
export const POINT_TONE_OF = (point: {
  kind: string
  fields: Record<string, unknown>
}): { bg: string; dot: string } => {
  if (point.kind === 'CLOSED' || point.kind === 'DEPOSIT_CLOSED' || point.kind === 'MATCHING_ENDED')
    return CLOSE_TONE(point.fields.successful === true)
  if (point.kind === 'MATCHING_REFUSED' && isOffer(point.fields)) return OFFER_TONE

  return POINT_TONE[point.kind] ?? NEUTRAL_TONE
}

/** Карточка отказа мэтчинга: у записи нет токена, адрес строится по id записи журнала. */
export const flowControlRefusalCardUrl = (id: number): string => '/matching/payout/refusal/' + id
